import React, {useCallback, useMemo, useEffect, useRef} from "react"
import {useTranslation} from "react-i18next"
import "../i18n"
import {makeStyles} from "@material-ui/core/styles"
import {useMediaQuery} from "@material-ui/core"
import {Divider, Typography, IconButton} from "@material-ui/core"
import {grey, pink} from "@material-ui/core/colors"
import SelectTarget from "./SelectTarget"
import SelectBaseDate from "./SelectBaseDate"
import SelectTerm from "./SelectTerm"
import ForwardOption from "./ForwardOption"
import SelectCategory from "./SelectCategory"
import {useStore, useDispatch} from "../Store"
import DownloadEquitySet from "./DownloadEquitySet"
import DownloadEquityCategorySet from "./DownloadEquityCategorySet"
import UploadCSV from "./UploadCSV"
import RiseFallBar from "./RiseFallBar"
import MovementAverageBar from "./MovementAverageBar"
import PasswordForm from "./PasswordForm"
import {SubPerspectiveBar, NormChart, BaseButton} from "../components"
import {isAmOne} from "../common"
import {useTheme} from "../hooks"

const useStyles = makeStyles(() => ({
  root: {
    display: `flex`,
    flexDirection: `column`,
    textAlign: `center`,
  },
  grid: {
    display: (props) => (props.isMobile ? `block` : `grid`),
    gridTemplateColumns: `350px 1fr`,
    margin: `0px 24px`,
  },
  title: {
    marginBottom: `4px`,
    fontWeight: `bold`,
    textAlign: `left`,
  },
  csvLabel: {
    display: `block`,
    margin: `8px 0px`,
    padding: 10,
    width: `100%`,
    color: `white`,
    backgroundColor: (props) => (props.password === process.env.CSV_UPDATE_PW ? pink.A400 : grey[500]),
    borderRadius: 4,
    cursor: `pointer`,
    transition: `background-color 0.2s`,
    "&:hover": {
      backgroundColor: (props) => (props.password === process.env.CSV_UPDATE_PW ? pink.A200 : grey[500]),
    },
  },
  csvButton: {
    display: `none`,
  },
  trendPanel: {
    display: (props) => (props.trendPanel === true ? `block` : `none`),
  },
  switch: {
    width: `100%`,
    justifyContent: `center`,
    marginTop: (props) => (props.isMobile === true ? 5 : 5),
  },
  subTabs: {
    textAlign: `center`,
    width: `100%`,
    margin: `0 auto`,
  },
  button: {
    width: `100%`,
  },
  pageRoot: {
    marginTop: `20px`,
  },
  sidebar: {
    paddingTop: '11px',
  }
}))

export default function SideMenu() {
  const {t} = useTranslation()
  const {
    store: {
      themeColor,
      tabs,
      perspective,
      trendPanel,
      password,
      activeTab,
      mainType,
      mainGroup,
      mainCategory,
      targetIds,
      termIds,
      dateIds,
      categoryIds,
      locale,
    },
  } = useStore()
  const theme = useTheme(themeColor)
  const isMobile = useMediaQuery(theme.breakpoints.down(`xs`))
  const classes = useStyles({
    password,
    trendPanel,
    isMobile,
  })
  const {dispatch} = useDispatch()
  const topOfSideMenu = useRef(null)

  const movementAverageBar = useMemo(() => <MovementAverageBar />, []) //移動平均バー
  const forwardOption = useMemo(() => <ForwardOption />, []) //騰落率バー

  // 「予測開始」ボタン押下時の処理
  const onGetSimilarClick = useCallback(() => {
    dispatch({
      type: `GET_SIMILAR`,
      getSimilar: true,
    })
  }, [])

  //タブ切り替え時のアクティブタグの保持
  useEffect(() => {
    Object.entries(tabs).forEach((k, v) => {
      activeTab[k] = v === perspective
    })
    dispatch({
      type: `ACTIVE_TAB`,
      activeTab,
    })
  }, [perspective])

  // 類似チャートタイトル
  const normChartTitle = useMemo(() => {
    return (
      <Typography className={classes.title}>
        {t(`titleChart`)}
      </Typography>
    )
  }, [locale])

  /** EQ系タブのコンテンツを作成 */
  const createEquityMenu = (tab, opt) => useMemo(() => (
      <div>
        <SelectTarget id={targetIds[tab]} />
        <SelectTerm id={termIds[tab]} />
        <SelectBaseDate id={dateIds[tab]} />
        <BaseButton className={classes.button} label={t(`getSimilar`)} onClick={onGetSimilarClick} />
        <RiseFallBar />
        <Divider
          style={{
            marginTop: `16px`,
          }}
        />
        {movementAverageBar}
        {(mainCategory[tab] !== `none`) && <SelectCategory id={categoryIds[tab]} />}
        {forwardOption}
        {opt && (
          <div>
            <DownloadEquitySet group={mainGroup[tab]} type={`equity_set`} />
            <DownloadEquityCategorySet group={mainGroup[tabs]} type={`equity_category`} />
            <PasswordForm group={mainGroup[tab]} />
            <UploadCSV kind={`EQUITY`} group={mainGroup[tab]} pw={process.env.CSV_UPDATE_PW} />
            <UploadCSV kind={`CATEGORY`} group={mainGroup[tab]} pw={process.env.CSV_UPDATE_PW} />
          </div>
        )}
        <br />
        <br />
      </div>
    ), [activeTab[tab], locale, themeColor]
  )

  // FX系タブのコンテンツを作成
  const createFxMenu = (tab, opt) => useMemo(() => (
      <div>
        <SelectTarget id={targetIds[tab]} />
        <SelectTerm id={termIds[tab]} />
        <SelectBaseDate id={dateIds[tab]} />
        <BaseButton className={classes.button} label={t(`getSimilar`)} onClick={onGetSimilarClick} />
        <RiseFallBar />
        <Divider
          style={{
            marginTop: `16px`,
          }}
        />
        {movementAverageBar}
        {forwardOption}
        {opt && <DownloadEquitySet group={mainGroup[tab]} type={`fx_set`} />}
        <br />
        <br />
      </div>
    ),
    [activeTab[tab], locale, themeColor],
  )

  // データタイプによるメニューの切り分け
  const menus = { }
  Object.entries(tabs).forEach((k, v) => {
    switch (mainType[v]) {
      case `FX`:
      case `FXSET`:
        menus[v] = createFxMenu(v, isAmOne())
        return
      case `EQ`:
      case `EQSET`:
        menus[v] = createEquityMenu(v, isAmOne())
        return
    }
  })

  return (
    <div className={classes.root} ref={topOfSideMenu}>
      {isMobile && <SubPerspectiveBar />}
      <div className={classes.grid}>
        <div className={classes.sidebar}>
          {normChartTitle}
          {menus[perspective ?? Object.keys(tabs)[0]]}
        </div>
        <div className={classes.subTabs}>
          {!isMobile && <SubPerspectiveBar />}
          <NormChart id={`normChart`} />
        </div>
      </div>
    </div>
  )
}
